import { Controller } from "@hotwired/stimulus"
import { Toast } from "bootstrap"

/**
 * Toast Manager Controller
 * 
 * Attach to the toast container to listen for custom toast:show events.
 * This allows any part of the app to trigger toasts via:
 * 
 *   document.dispatchEvent(new CustomEvent("toast:show", {
 *     detail: { message: "Hello!", type: "success", title: "Optional Title" }
 *   }))
 * 
 * Types: "success", "error", "warning", "info" (default)
 */
export default class extends Controller {
  static values = {
    delay: { type: Number, default: 3000 }
  }

  connect() {
    this.boundShowToast = this.showToast.bind(this)
    document.addEventListener("toast:show", this.boundShowToast)
  }

  disconnect() {
    document.removeEventListener("toast:show", this.boundShowToast)
  }

  showToast(event) {
    const { message, title, type = "info" } = event.detail || {}
    if (!message) return

    const template = document.getElementById("toast-template")
    if (!template) return

    const clone = template.content.cloneNode(true)
    const toastEl = clone.querySelector(".toast")

    // Set content
    const bodyEl = toastEl.querySelector("[data-toast-body]")
    if (bodyEl) bodyEl.textContent = message

    const titleEl = toastEl.querySelector("[data-toast-title]")
    if (titleEl) titleEl.textContent = title || this.getDefaultTitle(type)

    // Set icon based on type
    const iconEl = toastEl.querySelector("[data-toast-icon]")
    if (iconEl) iconEl.className = `bi ${this.getIconClass(type)} me-2`

    // Add to container
    this.element.appendChild(toastEl)

    // Initialize and show Bootstrap toast
    const toast = new Toast(toastEl, { delay: this.delayValue })
    toast.show()

    // Remove from DOM after hidden
    toastEl.addEventListener("hidden.bs.toast", () => {
      toastEl.remove()
    })
  }

  getIconClass(type) {
    const icons = {
      success: "bi-check-circle-fill text-success",
      error: "bi-exclamation-circle-fill text-danger",
      warning: "bi-exclamation-triangle-fill text-warning",
      info: "bi-info-circle-fill text-info"
    }
    return icons[type] || icons.info
  }

  getDefaultTitle(type) {
    const titles = {
      success: "Success",
      error: "Error",
      warning: "Warning",
      info: "Info"
    }
    return titles[type] || titles.info
  }
}
